import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Spinner,
  Button
} from 'reactstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import api from '../../config';


// Import fallback image
import eventSvg from '../assets/images/event-svg.png';const EventGallary = () => {
  const [pastEvents, setPastEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    fetchPastEvents(1, true); // Initial load
  }, []);

  const fetchPastEvents = async (page = 1, isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);
      
      const response = await axios.get(`/api/get/past-events?page=${page}&limit=12`);
      
      if (response.data.isOk) {
        const { data, pagination } = response.data;
        
        if (isInitialLoad) {
          setPastEvents(data);
        } else {
          // Append new events to existing ones
          setPastEvents(prevEvents => [...prevEvents, ...data]);
        }
        
        setPagination(pagination);
        setHasMore(pagination.hasMore);
        setCurrentPage(page);
      } else {
        setError('Failed to load past events');
      }
    } catch (err) {
      console.error('Error fetching past events:', err);
      setError(err.response?.data?.message || 'Failed to load past events');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchPastEvents(currentPage + 1, false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  if (loading) {
    return (
      <section className="past-events py-5">
        <Container>
          <div className="text-center">
            <Spinner color="primary" />
            <p className="mt-3">Loading past events...</p>
          </div>
        </Container>
      </section>
    );
  }

  if (error) {
    return (
      <section className="past-events py-5">
        <Container>
          <div className="text-center">
            <h3>Unable to load past events</h3>
            <p>{error}</p>
          </div>
        </Container>
      </section>
    );
  }

  return (
    <section className="past-events py-3 py-lg-5">
      <Container>
        <div className="text-center mb-3 mb-lg-5">
          <h2 className="section-title">Past Events Gallery</h2>
          <p className="section-subtitle">A look back at our successful events around the globe</p>
        </div>

        <Row className="g-4">
          {pastEvents.length > 0 ? (
            pastEvents.map((event) => (
              <Col lg={4} md={6} key={event._id}>
                <div className="past-event-card">
                  <div className="past-event-img">
                    <img 
                      src={event.bannerImage ? `${api.API_URL}/${event.bannerImage}` : eventSvg}
                      className="img-fluid" 
                      alt={event.title}
                      onError={(e) => {
                        e.target.src = eventSvg;
                      }}
                    />
                  </div>
                  <div className="event-info text-center mt-3">
                    <h5 className="event-title">{event.title}</h5>
                    <p className="event-meta">
                      {`${event.cityId?.cityName || ''}, ${event.countryId?.countryName || ''}`} | {formatDate(event.eventDate)}
                    </p>

                    <div className="see-more my-3">
                      <Link to={`/event/${event.slug}`} className="see-more-link">
                        See More <i className="fas fa-arrow-right ms-1"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              </Col>
            ))
          ) : (
            <Col xs={12}>
              <div className="text-center">
                <p>No past events found.</p>
              </div>
            </Col>
          )}
        </Row>

        {/* Show More Button */}
        {pastEvents.length > 0 && hasMore && (
          <Row className="mt-4 mt-lg-5">
            <Col xs={12} className="text-center">
              <Button 
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="show-more-btn btn-outline-dark border-2 px-4 py-2 fw-semibold text-uppercase"
                style={{
                  borderRadius: '50px',
                  transition: 'all 0.3s ease',
                  fontSize: '14px',
                  letterSpacing: '0.5px',
                  minWidth: '200px',
                  backgroundColor: loadingMore ? 'transparent' : 'transparent',
                  borderColor: '#212529',
                  color: '#212529'
                }}
                onMouseEnter={(e) => {
                  if (!loadingMore) {
                    e.target.style.backgroundColor = '#212529';
                    e.target.style.color = '#ffffff';
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 4px 15px rgba(33, 37, 41, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loadingMore) {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#212529';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                {loadingMore ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Loading More Events...
                  </>
                ) : (
                  <>
                    Show More Events
                    <i className="fas fa-chevron-down ms-2" style={{ fontSize: '12px' }}></i>
                  </>
                )}
              </Button>
            </Col>
          </Row>
        )}

        {/* Events counter */}
        {pastEvents.length > 0 && pagination.totalEvents && (
          <Row className="mt-3">
            <Col xs={12} className="text-center">
              <small className="text-muted">
                Showing {pastEvents.length} of {pagination.totalEvents} events
              </small>
            </Col>
          </Row>
        )}
      </Container>
    </section>
  );
};

export default EventGallary;
